import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Card,
  Tabs,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import costsClaimApi from 'api/costsClaimApi';

const { TabPane } = Tabs;

export const ClaimMasterData: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'expense-types' | 'payment-methods' | 'banks'>(
    'expense-types',
  );
  const [dataList, setDataList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      let res: any;
      if (activeTab === 'expense-types') {
        res = await costsClaimApi.getExpenseTypes(false);
      } else if (activeTab === 'payment-methods') {
        res = await costsClaimApi.getPaymentMethods(false);
      } else {
        res = await costsClaimApi.getBanks(false);
      }
      setDataList(res?.data || []);
    } catch (err: any) {
      message.error('Không thể tải danh mục master data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item?: any) => {
    setEditingItem(item || null);
    setModalVisible(true);
    if (item) {
      form.setFieldsValue({
        code: item.code,
        nameVi: item.nameVi || item.name,
        nameEn: item.nameEn,
        isActive: item.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ isActive: true });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        if (activeTab === 'expense-types') {
          await costsClaimApi.updateExpenseType(editingItem.id, values);
        } else if (activeTab === 'payment-methods') {
          await costsClaimApi.updatePaymentMethod(editingItem.id, values);
        } else {
          await costsClaimApi.updateBank(editingItem.id, values);
        }
        message.success('Cập nhật thành công');
      } else {
        if (activeTab === 'expense-types') {
          await costsClaimApi.createExpenseType(values);
        } else if (activeTab === 'payment-methods') {
          await costsClaimApi.createPaymentMethod(values);
        } else {
          await costsClaimApi.createBank(values);
        }
        message.success('Tạo thành công');
      }
      setModalVisible(false);
      loadData();
    } catch (err) {
      // Validation error
    }
  };

  const handleDelete = async (id: number) => {
    try {
      if (activeTab === 'expense-types') {
        await costsClaimApi.deleteExpenseType(id);
      } else if (activeTab === 'payment-methods') {
        await costsClaimApi.deletePaymentMethod(id);
      } else {
        await costsClaimApi.deleteBank(id);
      }
      message.success('Đã vô hiệu hóa');
      loadData();
    } catch (err) {
      message.error('Xóa thất bại');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Mã', dataIndex: 'code', key: 'code' },
    {
      title: 'Tên (Việt / Tên chính)',
      dataIndex: 'nameVi',
      key: 'nameVi',
      render: (val: string, record: any) => val || record.name || '-',
    },
    {
      title: 'Tên (Anh)',
      dataIndex: 'nameEn',
      key: 'nameEn',
      render: (val: string) => val || '-',
    },
    {
      title: 'Kích hoạt',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (act: boolean) =>
        act ? (
          <span style={{ color: '#52c41a' }}>Đang dùng</span>
        ) : (
          <span style={{ color: '#ff4d4f' }}>Đã ẩn</span>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenModal(record)}>
            Sửa
          </Button>
          <Popconfirm title="Xác nhận ẩn mục này?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              Ẩn
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Quản lý Danh mục (Master Data)"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          Thêm Mới
        </Button>
      }
    >
      <Tabs activeKey={activeTab} onChange={(k: any) => setActiveTab(k)}>
        <TabPane tab="Loại Chi Phí (Expense Types)" key="expense-types" />
        <TabPane tab="Phương Thức Thanh Toán" key="payment-methods" />
        <TabPane tab="Danh Sách Ngân Hàng" key="banks" />
      </Tabs>

      <Table
        columns={columns}
        dataSource={dataList}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editingItem ? 'Sửa Danh mục' : 'Thêm Danh mục Mới'}
        visible={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Mã" rules={[{ required: true, message: 'Nhập mã' }]}>
            <Input placeholder="Ví dụ: TRAVEL, HOTEL, VCB..." />
          </Form.Item>
          <Form.Item
            name="nameVi"
            label="Tên (Tiếng Việt / Tên đầy đủ)"
            rules={[{ required: true, message: 'Nhập tên' }]}
          >
            <Input placeholder="Ví dụ: Chi phí đi lại, Chuyển khoản ngân hàng..." />
          </Form.Item>
          <Form.Item name="nameEn" label="Tên (Tiếng Anh)">
            <Input placeholder="Travel expense..." />
          </Form.Item>
          <Form.Item name="isActive" label="Trạng thái kích hoạt" valuePropName="checked">
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ClaimMasterData;
